import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Badge,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { FaSolarPanel, FaLeaf, FaDollarSign, FaBolt, FaHome, FaChartLine } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesSolarLoans: React.FC = () => {
  const environmentalBenefits = [
    {
      icon: FaLeaf,
      title: "Reduce Carbon Footprint",
      stat: "4.5 tonnes",
      description: "Average CO2 reduction per year with residential solar",
    },
    {
      icon: FaSolarPanel,
      title: "Clean Energy Generation",
      stat: "6,500 kWh",
      description: "Annual clean energy produced by 6kW system",
    },
    {
      icon: FaHome,
      title: "Energy Independence",
      stat: "70-90%",
      description: "Potential reduction in grid dependence",
    },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/client/finance/services/solar-loans"
        fallbackTitle="Solar Loans - Finance Your Solar Power System"
        fallbackDescription="Go solar and save on electricity bills with flexible solar financing. Pay off your system with the savings. 0% interest options available."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              Solar Finance Solutions
            </Badge>
            <Heading as="h1" size="2xl" fontWeight="bold">
              Finance Your Solar Power System
            </Heading>
            <Text fontSize="xl" maxW="3xl" opacity={0.95}>
              Make the switch to clean, renewable energy with flexible solar financing.
              Pay off your system with the money you save on electricity bills.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ opacity: 0.9 }}
              >
                Calculate Savings
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Get Solar Quote
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Payback Comparison Table */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="gray.800">
                Solar System Payback Comparison
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">
                See how quickly you can pay off your solar system with electricity savings.
                Most systems pay for themselves in 3-5 years.
              </Text>
            </VStack>

            <TableContainer w="full" border="1px solid" borderColor="gray.200" borderRadius="xl">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>System Size</Th>
                    <Th isNumeric>System Cost</Th>
                    <Th isNumeric>Annual Savings*</Th>
                    <Th isNumeric>Payback Period</Th>
                    <Th isNumeric>25-Year Savings</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontWeight="semibold">3kW System</Td>
                    <Td isNumeric>$4,500</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$750</Td>
                    <Td isNumeric>6 years</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$14,250</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td fontWeight="semibold">6kW System</Td>
                    <Td isNumeric>$7,500</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$1,500</Td>
                    <Td isNumeric>5 years</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$30,000</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="semibold">10kW System</Td>
                    <Td isNumeric>$11,000</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$2,500</Td>
                    <Td isNumeric>4.4 years</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$51,500</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td fontWeight="semibold">15kW System</Td>
                    <Td isNumeric>$15,500</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$3,750</Td>
                    <Td isNumeric>4.1 years</Td>
                    <Td isNumeric color={NAVY} fontWeight="bold">$78,250</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              *Estimated annual savings based on $0.30/kWh grid electricity rate and $0.05/kWh feed-in tariff.
              Actual savings vary by location, system performance, and energy consumption patterns.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Environmental Benefits */}
      <Box py={20} bg={SAND_LIGHT}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">
                Environmental Impact
              </Badge>
              <Heading as="h2" size="xl" color="gray.800">
                The Green Benefits of Solar
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">
                Going solar isn't just good for your wallet - it's great for the planet.
                Here's the environmental impact of installing a solar system.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              {environmentalBenefits.map((benefit, index) => (
                <Box
                  key={index}
                  bg="white"
                  p={8}
                  borderRadius="xl"
                  shadow="md"
                  textAlign="center"
                  border="2px solid"
                  borderColor={SAND}
                >
                  <VStack spacing={4}>
                    <Icon as={benefit.icon} boxSize={12} color={SAND} />
                    <Text fontSize="3xl" fontWeight="bold" color={NAVY}>
                      {benefit.stat}
                    </Text>
                    <Heading as="h3" size="md" color="gray.800">
                      {benefit.title}
                    </Heading>
                    <Text color="gray.600">{benefit.description}</Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>

            <Box
              bg="white"
              p={8}
              borderRadius="xl"
              border="2px solid"
              borderColor={SAND}
              w="full"
              maxW="3xl"
            >
              <VStack spacing={4} textAlign="center">
                <Icon as={FaLeaf} boxSize={10} color={SAND} />
                <Heading size="md" color="gray.800">
                  Equivalent Environmental Impact
                </Heading>
                <Text color="gray.600">
                  A 6kW solar system over 25 years is equivalent to:
                </Text>
                <SimpleGrid columns={3} spacing={6} w="full" pt={4}>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color={NAVY}>
                      52
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Trees planted
                    </Text>
                  </VStack>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color={NAVY}>
                      112,500
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      km NOT driven
                    </Text>
                  </VStack>
                  <VStack>
                    <Text fontSize="2xl" fontWeight="bold" color={NAVY}>
                      47,500
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      kg coal saved
                    </Text>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Financing Options */}
      <Box py={20}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">
                Flexible Finance
              </Badge>
              <Heading as="h2" size="xl" color="gray.800">
                Solar Loan Features
              </Heading>
              <Text fontSize="lg" color="gray.600">
                Our solar loans are designed to make clean energy affordable for everyone.
                Start saving from day one.
              </Text>
              <List spacing={4}>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaDollarSign} color={SAND} mt={1} boxSize={5} />
                  <Text>
                    <strong>0% Interest Options:</strong> Available for systems up to $10,000 (conditions apply)
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaBolt} color={SAND} mt={1} boxSize={5} />
                  <Text>
                    <strong>Immediate Savings:</strong> Start saving on electricity bills from installation day
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaChartLine} color={SAND} mt={1} boxSize={5} />
                  <Text>
                    <strong>Flexible Terms:</strong> 2 to 7 year repayment periods to suit your budget
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaSolarPanel} color={SAND} mt={1} boxSize={5} />
                  <Text>
                    <strong>No Deposit Options:</strong> 100% finance available for approved customers
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={FaHome} color={SAND} mt={1} boxSize={5} />
                  <Text>
                    <strong>Battery Ready:</strong> Finance solar + battery storage in one package
                  </Text>
                </ListItem>
              </List>
            </VStack>

            <Box bg={NAVY} p={10} borderRadius="2xl" color="white">
              <VStack spacing={6} align="start">
                <Heading as="h3" size="lg">
                  Example Solar Loan
                </Heading>
                <Text opacity={0.9}>
                  See how affordable solar can be with our flexible financing options:
                </Text>
                <VStack align="stretch" spacing={4} w="full" pt={4}>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">System Size:</Text>
                    <Text>6.6kW Premium</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">System Cost:</Text>
                    <Text>$7,990</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Loan Term:</Text>
                    <Text>5 years</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Interest Rate:</Text>
                    <Text>5.99% p.a.*</Text>
                  </HStack>
                  <HStack justify="space-between" pt={4} borderTop="1px solid" borderColor="whiteAlpha.400">
                    <Text fontWeight="bold" fontSize="lg">
                      Monthly Repayment:
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      $154
                    </Text>
                  </HStack>
                  <HStack justify="space-between" pb={4} borderBottom="1px solid" borderColor="whiteAlpha.400">
                    <Text fontWeight="semibold">Est. Monthly Savings:</Text>
                    <Text fontWeight="semibold" color={SAND}>
                      $180
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold" fontSize="xl">
                      Net Monthly Benefit:
                    </Text>
                    <Text fontWeight="bold" fontSize="xl" color={SAND}>
                      +$26
                    </Text>
                  </HStack>
                </VStack>
                <Text fontSize="sm" opacity={0.8} pt={4}>
                  *Standard rate. 0% interest options available for eligible customers.
                  Comparison rate and full terms available on application.
                </Text>
                <Button
                  size="lg"
                  bg={SAND}
                  color={NAVY}
                  w="full"
                  _hover={{ opacity: 0.9 }}
                >
                  Get My Solar Quote
                </Button>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={20} bg={NAVY} color="white">
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Icon as={FaSolarPanel} boxSize={16} />
            <Heading as="h2" size="xl">
              Ready to Go Solar and Start Saving?
            </Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.95}>
              Get a free solar assessment and personalized quote. See exactly how much you can
              save on electricity bills while helping the environment.
            </Text>
            <HStack spacing={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ opacity: 0.9 }}
              >
                Get Free Solar Assessment
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Call Solar Experts
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesSolarLoans;
